import React, { useState } from "react";
import {
    RotateCcw,
    History,
    Pin,
    Palette,
    Loader2,
    Check,
    X,
    Pencil,
    Globe,
    AlertTriangle,
} from "lucide-react";
import { Button } from "@shadcn-in-nx/ui/button";
import { Input } from "@shadcn-in-nx/ui/input";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { ScrollArea } from "@shadcn-in-nx/ui/scroll-area";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@shadcn-in-nx/ui/popover";
import { cn } from "@utils";
import { useTranslation } from "react-i18next";
import VersionChanges from "./VersionChanges";

function VersionHistoryPanel({
    versionHistory,
    isLoading,
    isLoadingRollback,
    onRollback,
    onUpdateVersion,
    onLoadMore,
    hasMore,
    selectedEnvironment,
    stepId,
    missingSecretWarnings = [],
    onDismissWarnings,
}) {
    const { t } = useTranslation();

    const [editingVersionId, setEditingVersionId] = useState(null);
    const [editName, setEditName] = useState("");
    const [isSavingName, setIsSavingName] = useState(false);

    const handleStartEdit = (version, e) => {
        e.stopPropagation();
        setEditingVersionId(version.id);
        setEditName(version.name || `Version ${version.version}`);
    };

    const handleCancelEdit = (e) => {
        if (e) e.stopPropagation();
        setEditingVersionId(null);
        setEditName("");
    };

    const handleSaveEdit = async (e) => {
        if (e) e.stopPropagation();
        if (!editingVersionId) return;

        setIsSavingName(true);
        const success = await onUpdateVersion(editingVersionId, {
            name: editName,
        });
        setIsSavingName(false);

        if (success) {
            setEditingVersionId(null);
            setEditName("");
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            handleSaveEdit(e);
        } else if (e.key === "Escape") {
            handleCancelEdit(e);
        }
    };

    const COLORS = [
        { name: "Red", value: "#ef4444" },
        { name: "Orange", value: "#f97316" },
        { name: "Amber", value: "#f59e0b" },
        { name: "Green", value: "#22c55e" },
        { name: "Blue", value: "#3b82f6" },
        { name: "Indigo", value: "#6366f1" },
        { name: "Purple", value: "#a855f7" },
        { name: "Pink", value: "#ec4899" },
    ];

    // Use versionHistory directly as backend handles sorting
    const sortedHistory = versionHistory;

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString();
    };

    // Check if the selected environment is in the version
    const isEnvironmentInVersion = (version) => {
        // Check production
        if (
            selectedEnvironment === "production" ||
            selectedEnvironment === "prod" ||
            selectedEnvironment == null
        ) {
            return version.isInProduction;
        }

        // Check staging environments
        if (
            version.stagingEnvironments?.some(
                (env) =>
                    env.name?.toLowerCase() ===
                    selectedEnvironment.toLowerCase()
            )
        ) {
            return true;
        }

        // Check development environments
        if (
            version.developmentEnvironments?.some(
                (env) =>
                    env.name?.toLowerCase() ===
                    selectedEnvironment.toLowerCase()
            )
        ) {
            return true;
        }

        // Check custom environments
        if (
            version.customEnvironments?.some(
                (env) =>
                    env.name?.toLowerCase() ===
                    selectedEnvironment.toLowerCase()
            )
        ) {
            return true;
        }

        return false;
    };

    return (
        <div className="h-full flex flex-col bg-white">
            <div className="border-b p-4">
                <h2 className="text-lg font-semibold text-gray-900">
                    {t("stepsExecutionPanel.versionHistory") ||
                        "Version History"}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                    {t("stepsExecutionPanel.versionHistoryDescription") ||
                        "View and restore previous versions of your application"}
                </p>
            </div>
            {missingSecretWarnings.length > 0 && (
                <div className="flex items-start gap-2 mx-4 mt-3 p-3 rounded-lg bg-yellow-50 border border-yellow-200 text-yellow-800 text-sm">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                    <div className="flex-1">
                        <p className="font-medium">
                            {t(
                                "common.missingSecretsWarning",
                                "This version references secrets that are missing or have no value: {{secretNames}}. The build may fail or behave unexpectedly.",
                                {
                                    secretNames: missingSecretWarnings
                                        .map((w) => w.secretName)
                                        .join(", "),
                                }
                            )}
                        </p>
                    </div>
                    {onDismissWarnings && (
                        <button
                            onClick={onDismissWarnings}
                            className="shrink-0 text-yellow-600 hover:text-yellow-800"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    )}
                </div>
            )}
            <ScrollArea className="flex-1">
                <div className="p-4 pb-8 space-y-3">
                    {isLoading && sortedHistory.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                            <p className="text-sm">
                                Loading version history...
                            </p>
                        </div>
                    ) : sortedHistory.length > 0 ? (
                        sortedHistory.map((version) => (
                            <div
                                key={version.id}
                                className={cn(
                                    "flex flex-col gap-2 p-4 border rounded-lg bg-white hover:shadow-sm transition-shadow relative group",
                                    version.isPinned &&
                                        "border-primary/50 bg-primary/5"
                                )}
                                style={
                                    version.color
                                        ? {
                                              borderColor: version.color,
                                              backgroundColor: `${version.color}10`,
                                          }
                                        : {}
                                }
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-2">
                                            <Badge variant={"secondary"}>
                                                v{version.version}
                                            </Badge>
                                            {version.isInProduction && (
                                                <Badge variant="outline">
                                                    {t(
                                                        "stepsExecutionPanel.current"
                                                    ) || "Current"}
                                                </Badge>
                                            )}
                                            {version.isPinned && (
                                                <Badge
                                                    variant="secondary"
                                                    className="gap-1"
                                                >
                                                    <Pin className="h-3 w-3" />
                                                    Pinned
                                                </Badge>
                                            )}
                                            {version.isInProduction && (
                                                <Badge
                                                    variant="default"
                                                    className="gap-1 bg-green-600 hover:bg-green-700"
                                                >
                                                    <Globe className="h-3 w-3" />
                                                    {t(
                                                        "stepsExecutionPanel.production"
                                                    ) || "Production"}
                                                </Badge>
                                            )}
                                            {version.stagingEnvironments &&
                                                version.stagingEnvironments
                                                    .length > 0 &&
                                                version.stagingEnvironments.map(
                                                    (env) => (
                                                        <Badge
                                                            key={env.id}
                                                            variant="outline"
                                                            className="gap-1 border-blue-300 text-blue-700 bg-blue-50"
                                                        >
                                                            {env.name}
                                                        </Badge>
                                                    )
                                                )}
                                            {version.developmentEnvironments &&
                                                version.developmentEnvironments
                                                    .length > 0 &&
                                                version.developmentEnvironments.map(
                                                    (env) => (
                                                        <Badge
                                                            key={env.id}
                                                            variant="outline"
                                                            className="gap-1 border-yellow-300 text-yellow-700 bg-yellow-50"
                                                        >
                                                            {env.name}
                                                        </Badge>
                                                    )
                                                )}
                                            {version.customEnvironments &&
                                                version.customEnvironments
                                                    .length > 0 &&
                                                version.customEnvironments.map(
                                                    (env) => (
                                                        <Badge
                                                            key={env.id}
                                                            variant="outline"
                                                            className="gap-1 border-purple-300 text-purple-700 bg-purple-50"
                                                        >
                                                            {env.name}
                                                        </Badge>
                                                    )
                                                )}
                                        </div>
                                        {editingVersionId === version.id ? (
                                            <div
                                                className="flex items-center gap-1 mb-1"
                                                onClick={(e) =>
                                                    e.stopPropagation()
                                                }
                                            >
                                                <Input
                                                    value={editName}
                                                    onChange={(e) =>
                                                        setEditName(
                                                            e.target.value
                                                        )
                                                    }
                                                    onKeyDown={handleKeyDown}
                                                    className="h-7 text-sm px-2 py-1"
                                                    autoFocus
                                                    disabled={isSavingName}
                                                />
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-7 w-7 text-green-600 hover:text-green-700 hover:bg-green-50 shrink-0"
                                                    onClick={handleSaveEdit}
                                                    disabled={isSavingName}
                                                >
                                                    {isSavingName ? (
                                                        <Loader2 className="h-3 w-3 animate-spin" />
                                                    ) : (
                                                        <Check className="h-4 w-4" />
                                                    )}
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-7 w-7 text-red-600 hover:text-red-700 hover:bg-red-50 shrink-0"
                                                    onClick={handleCancelEdit}
                                                    disabled={isSavingName}
                                                >
                                                    <X className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        ) : (
                                            <div
                                                className="group/name flex items-center gap-2 cursor-pointer mb-1"
                                                onClick={(e) =>
                                                    handleStartEdit(version, e)
                                                }
                                                title="Click to rename"
                                            >
                                                <p className="text-sm font-medium text-gray-900">
                                                    {version.name ||
                                                        `Version ${version.version}`}
                                                </p>
                                                <Pencil className="h-3 w-3 text-gray-400 opacity-0 group-hover/name:opacity-100 transition-opacity" />
                                            </div>
                                        )}
                                        <p className="text-xs text-gray-500">
                                            {formatDate(version.createdAt)}
                                        </p>
                                        {version.environmentName && (
                                            <p className="text-xs text-gray-500 mt-1">
                                                {t(
                                                    "stepsExecutionPanel.changeMadeIn",
                                                    "Change made in {{env}}",
                                                    {
                                                        env: version.environmentName,
                                                    }
                                                )}
                                            </p>
                                        )}
                                        {version.changeNotes && (
                                            <p className="text-xs text-gray-600 mt-2 break-words">
                                                {version.changeNotes}
                                            </p>
                                        )}
                                        <VersionChanges
                                            versionId={version.id}
                                            stepId={stepId}
                                            isFirstVersion={version.version === 1}
                                        />
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <Popover>
                                            <PopoverTrigger asChild>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 text-gray-400 hover:text-gray-600"
                                                >
                                                    <Palette className="h-4 w-4" />
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-40 p-2">
                                                <div className="grid grid-cols-4 gap-1">
                                                    <button
                                                        className="w-8 h-8 rounded-full border border-gray-200 hover:scale-110 transition-transform"
                                                        onClick={() =>
                                                            onUpdateVersion(
                                                                version.id,
                                                                { color: null }
                                                            )
                                                        }
                                                        title="None"
                                                    >
                                                        <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
                                                            <span className="text-xs text-gray-400">
                                                                ×
                                                            </span>
                                                        </div>
                                                    </button>
                                                    {COLORS.map((color) => (
                                                        <button
                                                            key={color.value}
                                                            className="w-8 h-8 rounded-full border border-gray-200 hover:scale-110 transition-transform"
                                                            style={{
                                                                backgroundColor:
                                                                    color.value,
                                                            }}
                                                            onClick={() =>
                                                                onUpdateVersion(
                                                                    version.id,
                                                                    {
                                                                        color: color.value,
                                                                    }
                                                                )
                                                            }
                                                            title={color.name}
                                                        />
                                                    ))}
                                                </div>
                                            </PopoverContent>
                                        </Popover>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className={cn(
                                                "h-8 w-8 text-gray-400 hover:text-gray-600",
                                                version.isPinned &&
                                                    "text-primary hover:text-primary opacity-100"
                                            )}
                                            onClick={() =>
                                                onUpdateVersion(version.id, {
                                                    isPinned: !version.isPinned,
                                                })
                                            }
                                        >
                                            <Pin
                                                className={cn(
                                                    "h-4 w-4",
                                                    version.isPinned &&
                                                        "fill-current"
                                                )}
                                            />
                                        </Button>
                                        {!isEnvironmentInVersion(version) && (
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onRollback(version.id);
                                                }}
                                                disabled={isLoadingRollback}
                                            >
                                                <RotateCcw className="h-4 w-4 mr-1" />
                                                {t(
                                                    "stepsExecutionPanel.rollback"
                                                ) || "Restore"}
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="text-center py-12 text-muted-foreground">
                            <History className="h-12 w-12 mx-auto mb-3 opacity-50" />
                            <p className="text-sm">
                                {t("stepsExecutionPanel.noVersionHistory") ||
                                    "No version history available"}
                            </p>
                        </div>
                    )}
                    {hasMore && (
                        <div className="pt-2 pb-4 text-center">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={onLoadMore}
                                disabled={isLoading}
                                className="w-full"
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Loading...
                                    </>
                                ) : (
                                    "Load More"
                                )}
                            </Button>
                        </div>
                    )}
                </div>
            </ScrollArea>
        </div>
    );
}

export default VersionHistoryPanel;
